import React, { useState } from "react";
import { PlusCircleOutlined } from "@ant-design/icons";
import { ChangeLine } from "./ChangeLine";
import { KINDS, KindTag } from "./KindTag";
import { shortId, valueKey } from "./reviewAdapter";

/** Feminine nouns: « fusionnée dans », « supprimée ». */
const FEMININE = ["Address", "Institution", "Prestation", "Bill"];
const NOUN = {
  Address: "Cette adresse",
  Responsable: "Ce responsable",
  User: "Ce compte",
  Institution: "Cette institution",
  Prestation: "Cette prestation",
  Bill: "Cette facture",
  Batch: "Ce lot",
  Principal: "Ce mandant",
};

const agree = (model, word) => (FEMININE.includes(model) ? `${word}e` : word);

function IdChip({ id, isNew }) {
  if (isNew)
    return <span className="sv-id sv-id--new">nouvel identifiant</span>;
  if (!id) return null;
  return (
    <span className="sv-id" title={`identifiant ${id}`}>
      {shortId(id)}
    </span>
  );
}

function Head({ item, side, ribbon, targetBadge, quietAction }) {
  const exists = side === "before" ? item.before.exists : item.after.exists;
  const isAfter = side === "after";
  const k = KINDS[item.kind];
  return (
    <div className={`sv-head sv-head--${side}`}>
      {ribbon && (
        <div className={`sv-ribbon sv-ribbon--${side}`}>
          {isAfter ? "APRÈS" : "AVANT"}
        </div>
      )}
      <div className="sv-head-main">
        <div className="sv-head-title">
          <span className="sv-title-text">{item.title}</span>
          {exists && (
            <IdChip id={item.id} isNew={isAfter && !item.before.exists} />
          )}
        </div>
        {(item.subtitle || targetBadge) && (
          <div className="sv-head-sub">
            {item.subtitle}
            {item.subtitle && targetBadge ? " · " : ""}
            {targetBadge}
          </div>
        )}
      </div>
      {isAfter && k && (
        <div className="sv-head-kind">
          <KindTag kind={item.kind} />
        </div>
      )}
      {isAfter && item.action && item.action.label && !quietAction && (
        <div className={`sv-action sv-k-${item.kind}`}>
          <span className="sv-action-label">Action : </span>
          {item.action.label}
          {item.action.detail && !item.action.label.includes(item.action.detail)
            ? ` — ${item.action.detail}`
            : ""}
        </div>
      )}
    </div>
  );
}

/** The before card of a record that does not exist yet. */
function NotYet({ item }) {
  return (
    <div className="sv-void sv-void--before">
      <PlusCircleOutlined className="sv-void-icon sv-k-created" />
      <div className="sv-void-title">N'existe pas encore</div>
      <div className="sv-void-text">
        {NOUN[item.model] || "Cette fiche"} sera {agree(item.model, "créé")} en
        appliquant la fusion.
      </div>
    </div>
  );
}

/** The after card of a record that disappears (merged into another one, or deleted). */
function Gone({ item }) {
  const merged = item.kind === "merged" || !!item.mergedInto;
  const k = merged ? "merged" : "deleted";
  const Icon = KINDS[k].icon;
  // name the record by its own « Nom » when it has one (an address title may be its street)
  const nameLine = item.lines.find(
    l => l.path === "name" && l.before && typeof l.before.raw === "string",
  );
  const name = (nameLine && nameLine.before.raw.trim()) || item.title;
  return (
    <div className={`sv-void sv-void--after sv-k-${k}`}>
      <Icon className={`sv-void-icon sv-k-${k}`} />
      <div className="sv-void-title">
        « {name} » <IdChip id={item.id} />{" "}
        {merged ? (
          <>
            {agree(item.model, "fusionné")} dans{" "}
            {item.mergedInto ? (
              <>
                « {item.mergedInto.name} » <IdChip id={item.mergedInto.id} />
              </>
            ) : (
              "une autre fiche"
            )}
          </>
        ) : (
          agree(item.model, "supprimé")
        )}
      </div>
      <div className="sv-void-text">
        {merged
          ? "Les deux fiches n'en font plus qu'une : tout ce qui utilisait celle-ci utilisera l'autre. "
          : ""}
        La fiche disparaît — elle est gardée dans le journal et revient si vous
        restaurez.
      </div>
    </div>
  );
}

function Toggle({ hidden, open, onClick, word = "identique" }) {
  if (!hidden) return null;
  const many = `champs ${word}s`;
  const one = `champ ${word}`;
  return (
    <button type="button" className="sv-toggle" onClick={onClick}>
      {open
        ? `− masquer ${hidden > 1 ? `les ${hidden} ${many}` : `le ${one}`}`
        : `+ ${hidden} ${hidden > 1 ? many : one}`}
    </button>
  );
}

/**
 * One entity as a pair of cards: « Avant » on the left, « Après » on the right, one grid row per
 * field so both sides stay aligned line by line. Changed lines first; unchanged ones behind
 * « + N champs identiques ». `stacked` (narrow screens): Avant card above Après card, each with
 * its own ribbon.
 */
export function EntityPair({ item, stacked, targetBadge, compact }) {
  // unchanged fields stay folded even in « Tout » (hundreds of cards): one click opens a card
  const [open, setOpen] = useState(false);

  // records sections: the plain « rattaché » action is already said by the section sentence
  const quiet = !!compact && !!item.action && item.action.code === "repoint";
  const created = !item.before.exists;
  const gone = !item.after.exists;
  const changed = item.lines.filter(l => l.changed);
  const same = item.lines.filter(l => !l.changed);
  // a record that appears or disappears: every field IS the change — its empty fields fold away
  const all = created || gone;
  const isEmpty = l => valueKey(created ? l.after : l.before) === "";
  const filled = item.lines.filter(l => !isEmpty(l));
  const empties = item.lines.filter(isEmpty);
  const shown = all
    ? open
      ? filled.concat(empties)
      : filled
    : open
    ? changed.concat(same)
    : changed;
  const hidden = all ? empties.length : same.length;
  const word = all ? "vide" : "identique";
  const lineKinds = !all;
  const cls = `sv-pair sv-pair--${stacked ? "stacked" : "wide"} sv-pair--${
    item.kind
  }`;

  if (stacked) {
    const card = side => (
      <div className={`sv-card sv-card--${side}`}>
        <Head
          quietAction={quiet}
          item={item}
          side={side}
          ribbon
          targetBadge={side === "after" ? targetBadge : null}
        />
        {side === "before" && created ? (
          <NotYet item={item} />
        ) : side === "after" && gone ? (
          <Gone item={item} />
        ) : (
          <div className="sv-lines">
            {shown.map(l => (
              <ChangeLine
                key={l.path}
                line={
                  lineKinds
                    ? l
                    : { ...l, changed: side === "before" ? gone : false }
                }
                side={side}
              />
            ))}
            {!shown.length && !hidden && (
              <div className="sv-nolines">aucun champ</div>
            )}
          </div>
        )}
        {!(side === "before" && created) && !(side === "after" && gone) && (
          <Toggle
            hidden={hidden}
            word={word}
            open={open}
            onClick={() => setOpen(!open)}
          />
        )}
      </div>
    );
    return (
      <div className={cls} data-entity={item.key}>
        {card("before")}
        {card("after")}
      </div>
    );
  }

  // wide: one CSS grid, row 1 = headers, one row per shown line, last row = toggle;
  // the card backgrounds are two absolutely positioned layers spanning every row
  const bodyRows = shown.length || (all || !hidden ? 1 : 0);
  const rows = 1 + bodyRows + (hidden ? 1 : 0);
  const lineRow = i => ({ gridRow: i + 2 });
  const edge = i =>
    `${i === 0 ? " is-first" : ""}${
      i === shown.length - 1 && !hidden ? " is-last" : ""
    }`;
  return (
    <div
      className={cls}
      data-entity={item.key}
      style={{ gridTemplateRows: `repeat(${rows}, auto)` }}
    >
      <div
        className="sv-bg sv-bg--before"
        style={{ gridColumn: 1, gridRow: `1 / span ${rows}` }}
      />
      <div
        className={`sv-bg sv-bg--after sv-k-${item.kind}`}
        style={{ gridColumn: 2, gridRow: `1 / span ${rows}` }}
      />
      <div
        className="sv-cell sv-cell--head sv-cell--before"
        style={{ gridColumn: 1, gridRow: 1 }}
      >
        <Head quietAction={quiet} item={item} side="before" />
      </div>
      <div
        className="sv-cell sv-cell--head sv-cell--after"
        style={{ gridColumn: 2, gridRow: 1 }}
      >
        <Head
          quietAction={quiet}
          item={item}
          side="after"
          targetBadge={targetBadge}
        />
      </div>
      {created && (
        <div
          className="sv-cell sv-cell--before sv-cell--span"
          style={{
            gridColumn: 1,
            gridRow: `2 / span ${Math.max(shown.length, 1)}`,
          }}
        >
          <NotYet item={item} />
        </div>
      )}
      {gone && (
        <div
          className="sv-cell sv-cell--after sv-cell--span"
          style={{
            gridColumn: 2,
            gridRow: `2 / span ${Math.max(shown.length, 1)}`,
          }}
        >
          <Gone item={item} />
        </div>
      )}
      {shown.map((l, i) => (
        <React.Fragment key={l.path}>
          {!created && (
            <div
              className={`sv-cell sv-cell--line sv-cell--before${edge(i)}`}
              style={{ gridColumn: 1, ...lineRow(i) }}
            >
              <ChangeLine
                line={lineKinds ? l : { ...l, changed: gone }}
                side="before"
              />
            </div>
          )}
          {!gone && (
            <div
              className={`sv-cell sv-cell--line sv-cell--after${edge(i)}`}
              style={{ gridColumn: 2, ...lineRow(i) }}
            >
              <ChangeLine
                line={lineKinds ? l : { ...l, changed: false }}
                side="after"
              />
            </div>
          )}
        </React.Fragment>
      ))}
      {!shown.length && !all && !hidden && (
        <div
          className="sv-cell sv-cell--full"
          style={{ gridColumn: "1 / span 2", gridRow: 2 }}
        >
          <div className="sv-nolines">aucun champ</div>
        </div>
      )}
      {hidden > 0 && (
        <div
          className="sv-cell sv-cell--toggle"
          style={{ gridColumn: "1 / span 2", gridRow: rows }}
        >
          <Toggle
            hidden={hidden}
            word={word}
            open={open}
            onClick={() => setOpen(!open)}
          />
        </div>
      )}
    </div>
  );
}
